import { ApiError, type ApiErrorBody } from './errors';

/** Exportado porque o download de arquivo (SPEC-023 §8) não passa por `apiRequest`. */
export const BASE_URL = '/api/v1';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
  signal?: AbortSignal;
}

/**
 * Cliente HTTP único da aplicação.
 *
 * `credentials: 'same-origin'` é o que carrega a sessão: o token vive apenas no
 * cookie HttpOnly e é inacessível a JavaScript (RNF-F09, ADR-005). Não há —
 * nem pode haver — token em `localStorage`.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = options;
  const url = buildUrl(path, query);
  const isWrite = method !== 'GET';

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      credentials: 'same-origin',
      headers: {
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        // Cabeçalho customizado em toda escrita: formulário de outro site não
        // consegue enviá-lo, e é isso que barra CSRF (SPEC-017 §8).
        ...(isWrite ? { 'X-Requested-With': 'srp-web' } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (error) {
    // Rede indisponível não devolve envelope de erro; normalizamos para o mesmo
    // tipo, senão cada tela precisaria tratar dois formatos de falha.
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, { code: 'NETWORK_ERROR' });
  }

  if (response.status === 204) return undefined as T;

  const payload = await readJson(response);

  if (!response.ok) {
    throw new ApiError(response.status, extractErrorBody(payload));
  }

  return payload as T;
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = `${BASE_URL}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    // Só parâmetros com valor entram na query string (SPEC-014 §16).
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }

  const search = params.toString();
  return search ? `${url}?${search}` : url;
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export function extractErrorBody(payload: unknown): ApiErrorBody {
  if (
    typeof payload === 'object' &&
    payload !== null &&
    'error' in payload &&
    typeof (payload as { error: unknown }).error === 'object' &&
    (payload as { error: unknown }).error !== null
  ) {
    const body = (payload as { error: Record<string, unknown> }).error;
    return {
      code: typeof body.code === 'string' ? body.code : 'INTERNAL_ERROR',
      details: (body.details as Record<string, unknown> | undefined) ?? undefined,
      requestId: typeof body.requestId === 'string' ? body.requestId : null,
    };
  }

  // Resposta de erro fora do contrato (proxy, gateway): trata como interno.
  return { code: 'INTERNAL_ERROR' };
}
